import React, { useMemo } from 'react';
import {
  AbsoluteFill,
  Freeze,
  Html5Audio,
  OffthreadVideo,
  Sequence,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { parseMedia } from '@remotion/media-parser';
import { webReader } from '@remotion/media-parser/web';
import type { CalculateMetadataFunction } from 'remotion';
import type { VideoCommentaryProps } from './types';

const DEFAULT_WIDTH = 1920;
const DEFAULT_HEIGHT = 1080;
const DEFAULT_FPS = 30;

async function getAudioDuration(src: string): Promise<number> {
  const result = await parseMedia({
    src,
    fields: { durationInSeconds: true },
    reader: webReader,
  });
  return result.durationInSeconds ?? 0;
}

async function getVideoMeta(src: string): Promise<{ duration: number; width: number; height: number; fps: number }> {
  const result = await parseMedia({
    src,
    fields: { durationInSeconds: true, dimensions: true, fps: true },
    reader: webReader,
  });
  return {
    duration: result.durationInSeconds ?? 0,
    width: result.dimensions?.width ?? 0,
    height: result.dimensions?.height ?? 0,
    fps: result.fps ?? 0,
  };
}

export const calculateVideoCommentaryMetadata: CalculateMetadataFunction<
  VideoCommentaryProps
> = async ({ props }) => {
  // [fast-forward disabled] const speed = Math.max(1, props.fastForwardSpeed ?? 2);
  const speed = 1;
  const hasCompleteBackgroundVideoMeta =
    (props.backgroundVideo.width ?? 0) > 0 &&
    (props.backgroundVideo.height ?? 0) > 0 &&
    (props.backgroundVideo.fps ?? 0) > 0 &&
    (props.backgroundVideo.duration ?? 0) > 0;

  let parsedVideoMeta: Awaited<ReturnType<typeof getVideoMeta>> | null = null;
  if (!hasCompleteBackgroundVideoMeta) {
    try {
      parsedVideoMeta = await getVideoMeta(props.backgroundVideo.url);
    } catch (err) {
      console.warn(
        `Failed to parse background video metadata for ${props.backgroundVideo.url}:`,
        err,
      );
    }
  }

  const missingAudioDurationIndexes = props.comments
    .map((comment, index) => (comment.audioDuration == null ? index : -1))
    .filter((index) => index >= 0);

  const resolvedMissingAudioDurations =
    missingAudioDurationIndexes.length === 0
      ? []
      : await Promise.all(
          missingAudioDurationIndexes.map((index) =>
            getAudioDuration(props.comments[index].audioUrl),
          ),
        );

  const audioDurations = props.comments.map((comment, index) => {
    if (comment.audioDuration != null) {
      return comment.audioDuration;
    }

    const missingIndex = missingAudioDurationIndexes.indexOf(index);
    return missingIndex >= 0 ? resolvedMissingAudioDurations[missingIndex] : 0;
  });

  const explicitWidth = props.render?.width;
  const explicitHeight = props.render?.height;
  const sourceWidth = parsedVideoMeta?.width || props.backgroundVideo.width || 0;
  const sourceHeight = parsedVideoMeta?.height || props.backgroundVideo.height || 0;
  const width =
    explicitWidth && explicitWidth > 0
      ? explicitWidth
      : sourceWidth > 0
        ? sourceWidth
        : DEFAULT_WIDTH;
  const height =
    explicitHeight && explicitHeight > 0
      ? explicitHeight
      : sourceHeight > 0
        ? sourceHeight
        : DEFAULT_HEIGHT;
  const rawFps = parsedVideoMeta?.fps || props.backgroundVideo.fps || DEFAULT_FPS;
  const rawDuration = parsedVideoMeta?.duration || props.backgroundVideo.duration || 0;

  const fps = rawFps > 0 ? Math.round(rawFps) : DEFAULT_FPS;

  const commentsWithDuration = props.comments.map((c, i) => ({
    ...c,
    audioDuration: audioDurations[i],
  }));
  const sortedComments = [...commentsWithDuration].sort(
    (a, b) => a.startTime - b.startTime,
  );

  let totalFrames = 0;

  if (sortedComments.length > 0) {
    const firstGapFrames = Math.max(
      0,
      Math.round(sortedComments[0].startTime * fps),
    );
    totalFrames += Math.ceil(firstGapFrames / speed);

    sortedComments.forEach((comment, index) => {
      const commentFrames = Math.max(
        1,
        Math.ceil((comment.audioDuration ?? 0) * fps),
      );
      totalFrames += commentFrames;

      const nextComment = sortedComments[index + 1];
      if (!nextComment) {
        return;
      }
      const gapFrames = Math.max(
        0,
        Math.round((nextComment.startTime - comment.endTime) * fps),
      );
      totalFrames += Math.ceil(gapFrames / speed);
    });
  }

  const parsedVideoDuration =
    props.backgroundVideo.duration != null
      ? props.backgroundVideo.duration
      : rawDuration;
  const fallbackVideoDuration = Math.max(
    ...commentsWithDuration.map((c) => c.endTime),
    0,
  );
  const backgroundVideoDuration =
    parsedVideoDuration > 0 ? parsedVideoDuration : fallbackVideoDuration;

  return {
    durationInFrames: Math.max(totalFrames, 1),
    fps,
    width,
    height,
    props: {
      ...props,
      backgroundVideo: {
        ...props.backgroundVideo,
        duration: backgroundVideoDuration,
        width: sourceWidth > 0 ? sourceWidth : props.backgroundVideo.width,
        height: sourceHeight > 0 ? sourceHeight : props.backgroundVideo.height,
        fps,
      },
      comments: sortedComments,
    },
  };
};

interface SegmentLayout {
  type: 'comment' | 'fast-forward';
  from: number;
  segFrames: number;
  videoRangeFrames: number;
  startFromFrame: number;
  audioUrl?: string;
}

const SegmentVideo: React.FC<{
  backgroundVideoUrl: string;
  startFromFrame: number;
  videoRangeFrames: number;
  segmentType: SegmentLayout['type'];
  fastForwardSpeed: number;
}> = ({
  backgroundVideoUrl,
  startFromFrame,
  videoRangeFrames,
  segmentType,
  fastForwardSpeed,
}) => {
  const frame = useCurrentFrame();

  const relativeFrame =
    segmentType === 'fast-forward'
      ? Math.min(
          Math.floor(frame * fastForwardSpeed),
          Math.max(videoRangeFrames - 1, 0),
        )
      : Math.min(frame, Math.max(videoRangeFrames - 1, 0));

  return (
    <Freeze frame={relativeFrame}>
      <OffthreadVideo
        src={backgroundVideoUrl}
        trimBefore={startFromFrame}
        muted
      />
    </Freeze>
  );
};

export const VideoCommentary: React.FC<VideoCommentaryProps> = ({
  backgroundVideo,
  comments,
  voiceVolume,
  originalVolume,
  // [fast-forward disabled] fastForwardSpeed = 2,
  fastForwardSpeed: _fastForwardSpeed = 2,
}) => {
  const { fps } = useVideoConfig();
  const frame = useCurrentFrame();
  // [fast-forward disabled] 快进逻辑暂时禁用，恢复时将下行改为: const fastForwardSpeed = _fastForwardSpeed;
  const fastForwardSpeed = 1;

  const segments: SegmentLayout[] = useMemo(() => {
    const sortedComments = [...comments].sort((a, b) => a.startTime - b.startTime);
    let cumFrame = 0;
    const timeline: SegmentLayout[] = [];

    if (sortedComments.length === 0) {
      return timeline;
    }

    // Leading gap before first commentary is converted into fast-forward.
    const firstStartFrame = Math.max(0, Math.round(sortedComments[0].startTime * fps));
    if (firstStartFrame > 0) {
      const ffFrames = Math.max(1, Math.ceil(firstStartFrame / fastForwardSpeed));
      timeline.push({
        type: 'fast-forward',
        from: cumFrame,
        segFrames: ffFrames,
        videoRangeFrames: firstStartFrame,
        startFromFrame: 0,
      });
      cumFrame += ffFrames;
    }

    sortedComments.forEach((c, index) => {
      const audioDuration = c.audioDuration ?? 0;
      const commentFrames = Math.max(1, Math.ceil(audioDuration * fps));
      const videoRangeFrames = Math.max(
        1,
        Math.round((c.endTime - c.startTime) * fps),
      );

      timeline.push({
        type: 'comment',
        from: cumFrame,
        segFrames: commentFrames,
        videoRangeFrames,
        startFromFrame: Math.round(c.startTime * fps),
        audioUrl: c.audioUrl,
      });
      cumFrame += commentFrames;

      const nextComment = sortedComments[index + 1];
      if (!nextComment) {
        return;
      }

      const gapInFrames = Math.max(
        0,
        Math.round((nextComment.startTime - c.endTime) * fps),
      );

      if (gapInFrames > 0) {
        const ffFrames = Math.max(1, Math.ceil(gapInFrames / fastForwardSpeed));
        timeline.push({
          type: 'fast-forward',
          from: cumFrame,
          segFrames: ffFrames,
          videoRangeFrames: gapInFrames,
          startFromFrame: Math.round(c.endTime * fps),
        });
        cumFrame += ffFrames;
      }
    });

    return timeline;
  }, [comments, fps, fastForwardSpeed]);

  if (segments.length === 0) {
    return <AbsoluteFill style={{ backgroundColor: '#000' }} />;
  }

  let currentSeg = segments[segments.length - 1];
  for (const s of segments) {
    if (frame >= s.from && frame < s.from + s.segFrames) {
      currentSeg = s;
      break;
    }
  }

  const localFrame = frame - currentSeg.from;
  const ffVideoFrame =
    currentSeg.startFromFrame +
    Math.min(
      Math.floor(localFrame * fastForwardSpeed),
      Math.max(currentSeg.videoRangeFrames - 1, 0),
    );
  const commentVideoFrame =
    currentSeg.startFromFrame +
    Math.min(localFrame, Math.max(currentSeg.videoRangeFrames - 1, 0));
  const videoFrame =
    currentSeg.type === 'fast-forward' ? ffVideoFrame : commentVideoFrame;
  const totalBackgroundFrames = Math.max(
    1,
    Math.round((backgroundVideo.duration ?? 0) * fps),
  );
  const progress = Math.min(
    1,
    Math.max(0, videoFrame / Math.max(totalBackgroundFrames - 1, 1)),
  );
  return (
    <AbsoluteFill style={{ backgroundColor: '#000' }}>
      {segments.map((seg, index) => (
        <Sequence key={`video-${index}`} from={seg.from} durationInFrames={seg.segFrames}>
          <SegmentVideo
            backgroundVideoUrl={backgroundVideo.url}
            startFromFrame={seg.startFromFrame}
            videoRangeFrames={seg.videoRangeFrames}
            segmentType={seg.type}
            fastForwardSpeed={fastForwardSpeed}
          />
        </Sequence>
      ))}

      {/* {currentSeg.type === 'fast-forward' ? (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%) scale(${ffPulse})`,
            padding: '12px 16px',
            borderRadius: 14,
            background:
              'linear-gradient(180deg, rgba(26, 32, 44, 0.62), rgba(12, 17, 26, 0.56))',
            border: '1px solid rgba(255,255,255,0.22)',
            boxShadow: `0 10px 30px rgba(2, 132, 199, ${ffGlow}), inset 0 1px 0 rgba(255,255,255,0.18)`,
            minWidth: 260,
            zIndex: 12,
            userSelect: 'none',
            pointerEvents: 'none',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            color: '#e2e8f0',
            fontFamily:
              '"SF Pro Display", "Segoe UI", "PingFang SC", "Hiragino Sans GB", sans-serif',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 12,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: 'rgba(226,232,240,0.82)',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 4,
              }}
            >
              <span
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderLeft: '9px solid rgba(125, 211, 252, 0.95)',
                }}
              />
              <span
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderLeft: '9px solid rgba(125, 211, 252, 0.65)',
                }}
              />
            </div>
            <span>Fast Forward</span>
          </div>
          <div
            style={{
              marginTop: 6,
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={{
                fontSize: 30,
                lineHeight: 1,
                fontWeight: 700,
                letterSpacing: -0.5,
                color: '#f8fafc',
              }}
            >
              {fastForwardSpeed.toFixed(Number.isInteger(fastForwardSpeed) ? 0 : 1)}x
            </span>
            <span
              style={{
                fontSize: 12,
                color: 'rgba(226,232,240,0.74)',
              }}
            >
              segment playback
            </span>
          </div>
        </div>
      ) : null} */}

      <div
        style={{
          position: 'absolute',
          bottom: 24,
          left: 24,
          right: 24,
          height: 8,
          zIndex: 10,
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.28)',
            borderRadius: 999,
            overflow: 'hidden',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress * 100}%`,
              background: 'linear-gradient(90deg, #4fc3f7, #0288d1)',
              borderRadius: 999,
            }}
          />
        </div>

        {currentSeg.type === 'comment' && localFrame >= currentSeg.videoRangeFrames && (
          <div
            style={{
              position: 'absolute',
              left: `${progress * 100}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'rgba(2, 136, 209, 0.92)',
              boxShadow:
                '0 0 8px rgba(79, 195, 247, 0.6), 0 0 16px rgba(2, 136, 209, 0.35)',
              border: '2px solid rgba(255, 255, 255, 0.85)',
            }}
          >
            <div style={{ display: 'flex', gap: 2 }}>
              <div
                style={{
                  width: 3,
                  height: 9,
                  borderRadius: 1,
                  backgroundColor: '#fff',
                }}
              />
              <div
                style={{
                  width: 3,
                  height: 9,
                  borderRadius: 1,
                  backgroundColor: '#fff',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {segments.map((seg, index) => (
        <Sequence key={index} from={seg.from} durationInFrames={seg.segFrames}>
          {seg.type === 'comment' && originalVolume > 0 && (
            <Sequence durationInFrames={seg.videoRangeFrames}>
              <Html5Audio
                src={backgroundVideo.url}
                trimBefore={seg.startFromFrame}
                volume={originalVolume / 100}
              />
            </Sequence>
          )}
          {seg.type === 'comment' && seg.audioUrl ? (
            <Html5Audio src={seg.audioUrl} volume={voiceVolume / 100} />
          ) : null}
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};
